import { showErrToast } from "../components/Toasts";
import Logger from "../Logger";
import KnxRule from "./KnxRule";
import { MapSignalToTagResult } from "./KnxRules";
import Signal, { MatchType } from "./Signal";
import { getSignalTypeByName } from "./SignalTypes";

const logger = new Logger();

export type MapSignalMatchItem = {
  match?: string;
  matchRaw?: string;
  matchText?: string; // match only text-fragment (without function and dpt)
  matchDisplay?: string; // match only display-fragment (without function and dpt)
  signalNumber?: Array<number>;
  format?: string;
};

type TopLevelItem = string | Array<MapSignalMatchItem>;

function interpolateStringForRule(rule: KnxRule | undefined, matchId: string, s: string, params): string {
  const names = Object.keys(params);
  const vals = Object.values(params);
  try {
    return new Function(...names, `return \`${s}\`;`)(...vals);
  } catch (ex: any) {
    if (rule) {
      rule?.outputError(`format exception (${matchId}): ${ex.message}`, s, params);
    } else {
      showErrToast(`format exception (${matchId}): ${ex.message}`);
    }
    return "Error";
  }
}

export function formatMatch(
  rule: KnxRule | undefined,
  matchId: string,
  format: string,
  match: RegExpMatchArray,
  mapSignalRes: MapSignalToTagResult | undefined,
): string {
  function interpolate(s: string, params): string {
    return interpolateStringForRule(rule, matchId, s, params);
  }

  //
  // match with format insrtuction
  //

  const args = {};
  for (let i = 1; i < match.length; i++) {
    args[`m${i}`] = match[i];
  }
  // stelle "buttonNumber" für die format berechungung zur verfügung, insbesondere von "channelName"
  //
  args["buttonNumber"] = mapSignalRes?.buttonNumber || 0; // no itx

  let b = 999;
  let rockerNumber = 999;

  if (mapSignalRes?.buttonNumber) {
    if (typeof mapSignalRes.buttonNumber == "number") {
      b = mapSignalRes.buttonNumber;
    } else {
      b = parseInt(mapSignalRes.buttonNumber);
      if (isNaN(b)) {
        b = 999;
      }
    }
  }
  args["buttonNumber2"] = b == 999 ? 999 : (b - 1) * 2 + 1; // no itx
  args["rockerNumber"] = b == 999 ? 999 : Math.floor((b - 1) / 2) + 1; // no itx

  const res = interpolate(format, args);
  if (res == "" || res == "NaN") {
    rule?.outputError(`format failed (${matchId}):`, format, args);
  }
  return res;
}

export interface MapSignalParameters {
  single?: TopLevelItem;
  notSingle?: string;

  rocker?: TopLevelItem;
  notRocker?: string;

  statusLed?: TopLevelItem;
  notStatusLed?: string;

  button?: TopLevelItem;
  notButton?: string;

  logic?: TopLevelItem;
  notLogic?: string;

  logicInput?: TopLevelItem;
  notLogicInput?: string;

  logicOutput?: TopLevelItem;
  notLogicOutput?: string;

  actorChannel?: TopLevelItem;
  notActorChannel?: string;

  longPress?: TopLevelItem;
  extraLongPress?: TopLevelItem;
  temperature?: TopLevelItem;
  slap?: TopLevelItem;

  channelName?: TopLevelItem;
  notChannelName?: string;

  channelType?: TopLevelItem;
  notChanneType?: string;

  signalType?: TopLevelItem;
  notSignalType?: string;
}

export function mapSignal(
  rule: KnxRule,
  signal: Signal,
  params: MapSignalParameters,
  matchText?: string,
): MapSignalToTagResult {
  let res: MapSignalToTagResult = {};

  let signalText = signal.matchTextForMatchType(MatchType.Full);

  if (matchText) {
    signalText = matchText;
  }

  if (rule) {
    try {
      rule.output("mapSignal-params", params); // no itx

      res = mapSignalInner(params, signalText);

      rule.output("mapSignal-result", res); // no itx
    } catch (e) {
      logger.error("mapSignal failed:", e);
      rule.outputError(e);
    }
  }
  return res;

  function mapSignalInner(params: MapSignalParameters, matchText: string): MapSignalToTagResult {
    const mapSignalRes: MapSignalToTagResult = {};

    function match1(signalText: string, matchId: string): string | undefined {
      const res = matchMultiple(signalText, matchId);
      if (res) {
        if (Array.isArray(res)) {
          return res[0];
        } else {
          return res;
        }
      }
    }

    function matchMultiple(signalText: string, matchId: string): string | Array<string> | undefined {
      function interpolate(s: string, params): string {
        return interpolateStringForRule(rule, matchId, s, params);
      }

      const expression:
        | string
        | Array<string>
        | { match: string; format: string }
        | Array<{ match: string; format: string }> = params[matchId];

      const notExpression: string = params["not" + matchId.substring(0, 1).toUpperCase() + matchId.substring(1)];

      let notExpressionMatched = false;
      if (notExpression) {
        const match = signalText.match(new RegExp(notExpression, "i"));
        if (match) {
          notExpressionMatched = true;
        }
      }

      if (!notExpressionMatched && expression) {
        let expressions = new Array();

        if (!Array.isArray(expression)) {
          expressions = [expression];
        } else {
          expressions = expression;
        }

        for (const expressionX of expressions) {
          let expression: string | any;
          let match: any | undefined;

          if (expressionX.signalNumber) {
            const signalNumber = parseInt(signal.number);
            if (signalNumber >= expressionX.signalNumber[0] && signalNumber <= expressionX.signalNumber[1]) {
              match = [signal.number, signal.number];
            }
          } else {
            if (
              typeof expressionX == "object" &&
              (expressionX.match || expressionX.matchRaw || expressionX.matchText || expressionX.matchDisplay)
            ) {
              //
              // match with format insrtuction
              //

              if (expressionX.match) {
                expression = expressionX.match;
              } else if (expressionX.matchRaw) {
                expression = expressionX.matchRaw;
                signalText = signal.matchTextForMatchType(MatchType.Raw);
              } else if (expressionX.matchText) {
                expression = expressionX.matchText;
                signalText = signal.matchTextForMatchType(MatchType.Text);
              } else if (expressionX.matchDisplay) {
                expression = expressionX.matchDisplay;
                signalText = signal.matchTextForMatchType(MatchType.Display);
              }
            } else {
              expression = expressionX;
            }

            if (typeof expression == "string") {
              match = signalText.match(new RegExp(expression, "i"));
            } else {
              match = signalText.match(new RegExp(expression.source, "i"));
            }
          }

          if (match) {
            if (typeof expressionX == "object" && expressionX.format) {
              //
              // match with format insrtuction
              //

              const res = formatMatch(rule, matchId, expressionX.format, match, mapSignalRes);
              return res;
            } else {
              //
              // match WITHOUT fromat instruction
              //
              if (match.length > 2) {
                let res = new Array<string>();
                for (let i = 1; i < match.length; i++) {
                  res.push(match[i]);
                }
                return res;
              }
            }

            return match[1] || match[0]; // match[0] is for boolen matches without "()" for match parts
          }
        }
      }

      return undefined;
    }

    function sNtoButtonNumber(sN: string | Array<string> | undefined): number | string | undefined {
      function isNumber(value: string) {
        return typeof value === "number" && isFinite(value);
      }

      if (Array.isArray(sN)) {
        sN = sN.filter((s) => s && s.trim().length > 0);
      }

      if (sN) {
        let buttonNumber: string | number = 0;
        if (Array.isArray(sN)) {
          let allNumbers = true;
          for (let i = 0; i < sN.length; i++) {
            if (!sN[i]) {
              rule?.outputError(`Empty part in array: i=${i} sN=${sN.join(",")}`);
              continue;
            }

            // parseInt calculates in side-> parseInt("1/2")=> 1, parseInt("1.2")=> 1 etc.
            if (isNaN(parseInt(sN[i])) && sN[i].match(new RegExp("^[0-9 ]*$")) != null) {
              // no itx
              allNumbers = false;
              break;
            }
          }
          if (allNumbers) {
            for (let i = 0; i < sN.length; i++) {
              buttonNumber *= 10;
              buttonNumber += parseInt(sN[i]);
            }
          } else {
            buttonNumber = sN.join("");
          }
        } else {
          if (sN.match(new RegExp("^[0-9 ]*$"))) {
            buttonNumber = parseInt(sN);
          } else {
            buttonNumber = sN;
          }

          //
          // for text buttonnumber like "A", "B" etc
          //
          // if (isNaN(buttonNumber)) {
          //   buttonNumber = sN;
          // }
        }
        return buttonNumber;
      }

      return undefined;
    }

    try {
      let s: string | undefined = undefined;

      if (rule && rule.monitor) {
        rule.output(`Text to match: ${signalText} \n `);
      }
      ///////////////////////////////////////////////////////////////////////////
      //
      // button
      //
      ///////////////////////////////////////////////////////////////////////////

      let sN = matchMultiple(signalText, "single"); // no itx
      if (sN) {
        mapSignalRes.buttonNumber = sNtoButtonNumber(sN);
        mapSignalRes.buttonSide = "single";
      }

      sN = matchMultiple(signalText, "rocker"); // no itx
      if (sN) {
        mapSignalRes.buttonNumber = sNtoButtonNumber(sN);
        mapSignalRes.buttonSide = "both";
      }

      sN = matchMultiple(signalText, "button"); // no itx
      if (sN) {
        mapSignalRes.buttonNumber = sNtoButtonNumber(sN);
      }

      sN = matchMultiple(signalText, "statusLed"); // no itx
      if (sN) {
        mapSignalRes.statusNumber = sNtoButtonNumber(sN);
      }

      //
      // special buttons
      //

      if (params.slap) {
        const match = match1(signalText, "slap"); // no itx
        if (match) {
          mapSignalRes.buttonNumber = "slap"; // no itx
        }
      }

      if (params.temperature) {
        const match = match1(signalText, "temperature"); // no itx
        if (match) {
          //
          // MWE 9.12.2025
          // if (mapSignalRes.buttonNumber == undefined) erlaubt,
          // das die buttonDetection single:, rocker:, button gewinnt.
          // wenn sie definiert sind und matchen
          //
          if (mapSignalRes.buttonNumber == undefined) {
            mapSignalRes.buttonNumber = "temperature"; // no itx
          }
        }
      }

      //
      // button postfix
      //

      if (params.longPress) {
        const match = match1(signalText, "longPress"); // no itx
        if (match) {
          mapSignalRes.longPress = true;
        }
      }

      if (params.extraLongPress) {
        const match = match1(signalText, "extraLongPress"); // no itx
        if (match) {
          mapSignalRes.extraLongPress = true;
        }
      }

      ///////////////////////////////////////////////////////////////////////////
      //
      // (actor) channel
      //
      ///////////////////////////////////////////////////////////////////////////

      sN = matchMultiple(signalText, "actorChannel"); // no itx
      if (sN) {
        mapSignalRes.channelNumber = sNtoButtonNumber(sN);
      }

      ///////////////////////////////////////////////////////////////////////////
      //
      // logic
      //
      ///////////////////////////////////////////////////////////////////////////

      sN = matchMultiple(signalText, "logic");
      if (sN) {
        mapSignalRes.logicNumber = sNtoButtonNumber(sN);
      }

      s = match1(signalText, "logicInput");
      if (s) {
        mapSignalRes.logicInput = true;
      }

      s = match1(signalText, "logicOutput");
      if (s) {
        mapSignalRes.logicOutput = true;
      }

      ///////////////////////////////////////////////////////////////////////////
      //
      // (special) channel
      //
      ///////////////////////////////////////////////////////////////////////////

      sN = matchMultiple(signalText, "special"); // no itx
      if (sN) {
        mapSignalRes.specialNumber = sNtoButtonNumber(sN);
      }

      ///////////////////////////////////////////////////////////////////////////
      //
      // channelName for all (button/actor/logic)
      //
      ///////////////////////////////////////////////////////////////////////////

      sN = matchMultiple(signalText, "channelName"); // no itx
      if (sN) {
        if (Array.isArray(sN)) {
          mapSignalRes.channelName = sN.join(" ");
        } else {
          mapSignalRes.channelName = sN;
        }
      }

      s = match1(signalText, "channelType");
      if (s) {
        mapSignalRes.channelType = s;
      }
      s = match1(signalText, "signalType");
      if (s) {
        const signalType = getSignalTypeByName(s);
        if (!signalType) {
          throw new Error(`Unknown signal type: ${s}`);
        } else {
          if (signal.determinedFromId != "manual") {
            signal.oldType = signal.type;
            signal.type = signalType;
            signal.determinedFromId = "manualViaScript";
          }
        }
      }

      s = match1(signalText, "ignore"); // no itx
      if (s) {
        mapSignalRes.ignore = true;
      }
    } catch (e) {
      logger.log("ERROR: mapSignalToTag: ", e);
      rule?.outputError(e);
    }

    return mapSignalRes;
  }
}
